/*
 * GrowthBook storefront experiments.
 * Runs one experiment: recommendation_rail_pdp ("Recommendation Rail
 * Repositioning"), which moves the "You might also love" recommendation slider
 * on the Lip Tint Oil PDP to just after the love-features section when the
 * visitor is in the treatment bucket (Variation 1 = feature value 2).
 *
 * Also forwards events to the GrowthBook Managed Warehouse: any event logged via
 * gb.logEvent(name, props) is POSTed to the ingestor (this mirrors what the
 * official growthbookTrackingPlugin does, inlined here because the plugins are
 * only shipped as CJS/ESM and there is no standalone browser bundle to vendor).
 * The Love PDP fires "view_item" by setting window.GB_VIEW_ITEM (see
 * snippets/growthbook-view-item.liquid).
 */
(function () {
  'use strict';

  var SLIDER_SELECTOR = '[id^="shopify-section-"][id*="recommended_product"]';
  var ANCHOR_SELECTOR = 'section.love-features';
  var INGESTOR_HOST = 'https://us1.gb-ingest.com';
  var SDK_VERSION = '1.6.5'; // vendored growthbook.min.js version
  var SCROLL_DEPTHS = [10, 20, 30, 40]; // page-scroll-depth events: scroll_10 .. scroll_40
  var FEATURE_KEY = 'recommendation_rail_pdp';
  var CONTROL_VALUE = 1; // GrowthBook variation value for Control (slider stays at bottom)
  var TREATMENT_VALUE = 2; // GrowthBook variation value for Variation 1 (move slider up)
  var DONE_FLAG = 'gbSliderDone';

  // True when the visitor should see the moved slider. A manual QA override via
  // ?gb_slider=treatment|control wins; otherwise read the GrowthBook feature
  // (recommendation_rail_pdp: 1 = Control, 2 = Variation 1).
  function isTreatment(gb) {
    try {
      var override = new URLSearchParams(window.location.search).get('gb_slider');
      if (override === 'treatment') return true;
      if (override === 'control') return false;
    } catch (e) {}
    return gb ? Number(gb.getFeatureValue(FEATURE_KEY, CONTROL_VALUE)) === TREATMENT_VALUE : false;
  }

  function moveSliderAfterFeatures() {
    var slider = document.querySelector(SLIDER_SELECTOR);
    var anchor = document.querySelector(ANCHOR_SELECTOR);
    if (!slider || !anchor || document.body.dataset[DONE_FLAG]) return false;
    anchor.insertAdjacentElement('afterend', slider);
    document.body.dataset[DONE_FLAG] = '1';
    return true;
  }

  // True only on a PDP tagged "growthbook-test" (the snippet sets this flag).
  function isExperimentPdp() {
    return !!window.GB_PDP_EXPERIMENT;
  }

  // GrowthBook variation index for the recommendation_rail_pdp experiment:
  // 0 = Control, 1 = Variation 1 (treatment). Honors the QA override via isTreatment.
  function getVariationId(gb) {
    return isTreatment(gb) ? 1 : 0;
  }

  // Coarse device classification from the user agent.
  function getDeviceType() {
    try {
      var ua = navigator.userAgent || '';
      if (/iPad|Tablet|PlayBook|Silk/i.test(ua) || (/Android/i.test(ua) && !/Mobi/i.test(ua))) return 'tablet';
      if (/Mobi|Android|iPhone|iPod/i.test(ua)) return 'mobile';
    } catch (e) {}
    return 'desktop';
  }

  // Per-tab session id, persisted in sessionStorage for the life of the tab.
  function getSessionId() {
    try {
      var key = 'gb_session_id';
      var id = window.sessionStorage.getItem(key);
      if (!id) {
        id = (window.crypto && window.crypto.randomUUID)
          ? window.crypto.randomUUID()
          : 'gs-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
        window.sessionStorage.setItem(key, id);
      }
      return id;
    } catch (e) {
      return '';
    }
  }

  function runSliderExperiment(gb) {
    // Gate on the product tag (growthbook-test) exposed by the Liquid snippet.
    if (!isExperimentPdp()) return;
    // Only relevant where both nodes exist (the Lip Tint Oil PDP layout).
    if (!document.querySelector(ANCHOR_SELECTOR) || !document.querySelector(SLIDER_SELECTOR)) {
      return;
    }
    if (isTreatment(gb)) {
      moveSliderAfterFeatures();
    }
  }

  // Attribute keys the Managed Warehouse promotes to top-level columns; anything
  // else on the SDK attributes travels in context_json. Mirrors the official
  // growthbookTrackingPlugin parseAttributes().
  var TOP_LEVEL_ATTRS = ['user_id', 'device_id', 'anonymous_id', 'id', 'page_id',
    'session_id', 'utmCampaign', 'utmContent', 'utmMedium', 'utmSource', 'utmTerm', 'pageTitle'];

  // Builds the exact event payload the ingestor expects (event_name / properties_json
  // / flattened identity columns / context_json), matching growthbookTrackingPlugin.
  function buildEventPayload(eventName, properties, user) {
    var attrs = (user && user.attributes) || {};
    var nested = {};
    for (var k in attrs) {
      if (Object.prototype.hasOwnProperty.call(attrs, k) && TOP_LEVEL_ATTRS.indexOf(k) === -1) {
        nested[k] = attrs[k];
      }
    }
    return {
      event_name: eventName,
      properties_json: properties || {},
      user_id: attrs.user_id || null,
      device_id: attrs.device_id || attrs.anonymous_id || attrs.id || null,
      page_id: attrs.page_id || null,
      session_id: attrs.session_id || null,
      utm_campaign: attrs.utmCampaign || undefined,
      utm_content: attrs.utmContent || undefined,
      utm_medium: attrs.utmMedium || undefined,
      utm_source: attrs.utmSource || undefined,
      utm_term: attrs.utmTerm || undefined,
      page_title: attrs.pageTitle || undefined,
      sdk_language: 'js',
      sdk_version: SDK_VERSION,
      url: (user && user.url) || (window.location && window.location.href) || '',
      context_json: nested
    };
  }

  // Sends a single event to the GrowthBook Managed Warehouse ingestor. Called by
  // the SDK for every gb.logEvent(...); signature is (eventName, properties, user).
  function makeEventLogger(clientKey) {
    return function (eventName, properties, user) {
      if (!clientKey) return;
      var payload = [buildEventPayload(eventName, properties, user)];
      try {
        window.fetch(INGESTOR_HOST + '/track?client_key=' + encodeURIComponent(clientKey), {
          method: 'POST',
          body: JSON.stringify(payload),
          headers: { 'Accept': 'application/json', 'Content-Type': 'text/plain' },
          credentials: 'omit',
          keepalive: true
        }).catch(function () {});
      } catch (e) {}
    };
  }

  // Reads the product payload for a clicked recommendation-rail card. Prefers the
  // currently-selected swatch (data-* attributes) and falls back to the visible
  // price/title nodes so the payload works for every card layout in the slider.
  function extractCardViewItem(card, link) {
    if (!card) return null;
    function text(sel) { var el = card.querySelector(sel); return el ? el.textContent.trim() : ''; }
    var activeSwatch = card.querySelector('.pvc__swatch.is-active') || card.querySelector('[data-pvc-swatch]');
    var variantInput = card.querySelector('.product-variant-id');
    var productInput = card.querySelector('input[name="product-id"]');
    return {
      item_id: productInput ? productInput.value : '',
      item_name: text('.product-card-title'),
      item_subtitle: text('.product-card-subtitle'),
      variant_id: variantInput ? variantInput.value
        : (activeSwatch ? activeSwatch.getAttribute('data-variant-id') : ''),
      variant_title: activeSwatch ? activeSwatch.getAttribute('title') : '',
      price: activeSwatch ? activeSwatch.getAttribute('data-price') : text('.pvc__price-now'),
      url: link ? link.getAttribute('href') : '',
      source: FEATURE_KEY
    };
  }

  // Adds the identity/context params shared by every custom event.
  function withCommonParams(props, gb) {
    props.variationId = getVariationId(gb);
    props.device_id = window.GROWTHBOOK_ANON_ID || '';
    props.user_id = window.GB_USER_ID || '';
    props.timestamp = new Date().toISOString();
    props.session_id = getSessionId();
    props.device_type = getDeviceType();
    return props;
  }

  // Builds the card payload and fires a GrowthBook event for it. Shared by the
  // "view_item" (media click), "add_to_cart" (add button) and "variant_click" handlers.
  function fireCardEvent(gb, eventName, card, link) {
    var item = extractCardViewItem(card, link);
    if (!item) return;
    withCommonParams(item, gb);
    if (gb) { try { gb.logEvent(eventName, item); } catch (e) {} }
  }

  // Delegated click handler on the "You might also love" slider:
  //   - clicking the product media link fires "view_item"
  //   - clicking the "Add to cart" button fires "add_to_cart"
  //   - changing the variant (pack or swatch) fires "variant_click"
  // Uses gb.logEvent (keepalive fetch) so beacons survive any navigation.
  function bindRecommendationClicks(gb) {
    if (!isExperimentPdp()) return;
    var slider = document.querySelector(SLIDER_SELECTOR);
    if (!slider || slider.dataset.gbClickBound) return;
    slider.dataset.gbClickBound = '1';
    slider.addEventListener('click', function (e) {
      var target = e.target;
      if (!target || !target.closest) return;

      var addBtn = target.closest('button.pvc__add');
      if (addBtn && slider.contains(addBtn)) {
        var addCard = addBtn.closest('pack-variant-card') || addBtn.closest('.product-card-wrapper');
        var addLink = addCard ? addCard.querySelector('a.pvc__media') : null;
        fireCardEvent(gb, 'add_to_cart', addCard, addLink);
        return;
      }

      var variantBtn = target.closest('button.pvc__pack, button.pvc__swatch');
      if (variantBtn && slider.contains(variantBtn)) {
        var vCard = variantBtn.closest('pack-variant-card') || variantBtn.closest('.product-card-wrapper');
        var vLink = vCard ? vCard.querySelector('a.pvc__media') : null;
        // Defer a tick so the PVC component applies the new selection first, then
        // extractCardViewItem reads the freshly selected variant.
        setTimeout(function () { fireCardEvent(gb, 'variant_click', vCard, vLink); }, 0);
        return;
      }

      var link = target.closest('a.pvc__media');
      if (link && slider.contains(link)) {
        var card = link.closest('pack-variant-card') || link.closest('.product-card-wrapper');
        fireCardEvent(gb, 'view_item', card, link);
      }
    });
  }

  // Fires scroll_10 / scroll_20 / scroll_30 / scroll_40 once each as the visitor
  // reaches those full-page scroll-depth thresholds on the experiment PDP.
  function bindScrollDepth(gb) {
    if (!isExperimentPdp()) return;
    if (document.body.dataset.gbScrollBound) return;
    document.body.dataset.gbScrollBound = '1';

    var fired = {};
    var ticking = false;

    function currentDepth() {
      var doc = document.documentElement;
      var scrollTop = window.pageYOffset || doc.scrollTop || 0;
      var max = (doc.scrollHeight || document.body.scrollHeight || 0) - window.innerHeight;
      return max <= 0 ? 100 : Math.min(100, (scrollTop / max) * 100);
    }

    function check() {
      var depth = currentDepth();
      for (var i = 0; i < SCROLL_DEPTHS.length; i++) {
        var t = SCROLL_DEPTHS[i];
        if (depth >= t && !fired[t]) {
          fired[t] = 1;
          var props = withCommonParams({ percent: t, url: window.location.href }, gb);
          if (gb) { try { gb.logEvent('scroll_' + t, props); } catch (e) {} }
        }
      }
      // All thresholds reached: stop listening.
      if (fired[SCROLL_DEPTHS[SCROLL_DEPTHS.length - 1]]) {
        window.removeEventListener('scroll', onScroll);
      }
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () { ticking = false; check(); });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    check(); // handle short pages / restored scroll position
  }

  // Fires view_component_50 / view_component_100 once each when the recommendation
  // rail reaches 50% / 100% visibility in the viewport (via IntersectionObserver).
  function bindComponentVisibility(gb) {
    if (!isExperimentPdp() || !window.IntersectionObserver) return;
    var el = document.querySelector(SLIDER_SELECTOR);
    if (!el || el.dataset.gbViewBound) return;
    el.dataset.gbViewBound = '1';

    var fired = {};
    function fire(pct) {
      if (fired[pct]) return;
      fired[pct] = 1;
      var props = withCommonParams({ percent: pct, component: 'recommendation_rail', url: window.location.href }, gb);
      if (gb) { try { gb.logEvent('view_component_' + pct, props); } catch (e) {} }
    }

    var observer = new window.IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var ratio = entries[i].intersectionRatio;
        if (ratio >= 0.5) fire(50);
        if (ratio >= 0.99) fire(100); // 0.99 guards against float rounding at full visibility
      }
      if (fired[50] && fired[100]) observer.disconnect();
    }, { threshold: [0.5, 1] });

    observer.observe(el);
  }

  function whenDomReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function init() {
    var cfg = window.GROWTHBOOK_CONFIG || {};

    var SDK = window.growthbook && window.growthbook.GrowthBook;

    // No key configured or SDK missing: leave the page untouched (control layout).
    if (!SDK || !cfg.clientKey) {
      // Still honor the QA override even without the SDK.
      whenDomReady(function () {
        runSliderExperiment(null);
        bindRecommendationClicks(null);
        bindScrollDepth(null);
        bindComponentVisibility(null);
      });
      return;
    }

    var gb = new SDK({
      apiHost: cfg.apiHost || 'https://cdn.growthbook.io',
      clientKey: cfg.clientKey,
      // id is the bucketing key (feature rule hashAttribute is "id"); device_id /
      // user_id / session_id are promoted to top-level ingestor columns so every
      // event — including the SDK's "Experiment Viewed" exposures — is attributed.
      attributes: {
        id: window.GROWTHBOOK_ANON_ID || '',
        device_id: window.GROWTHBOOK_ANON_ID || '',
        user_id: window.GB_USER_ID || '',
        session_id: getSessionId(),
        deviceType: getDeviceType(),
        url: window.location.href
      },
      eventLogger: makeEventLogger(cfg.clientKey)
    });

    gb.init({ timeout: 2000 }).then(function () {
      whenDomReady(function () {
        runSliderExperiment(gb);
        bindRecommendationClicks(gb);
        bindScrollDepth(gb);
        bindComponentVisibility(gb);
      });
    }).catch(function () {
      // Network/SDK failure: fall back to control (and QA override if present).
      whenDomReady(function () {
        runSliderExperiment(null);
        bindRecommendationClicks(null);
        bindScrollDepth(null);
        bindComponentVisibility(null);
      });
    });
  }

  init();
})();
